import { useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";

const F = "w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-emerald-600";
const L = "block text-xs font-600 uppercase tracking-wide text-zinc-500 mb-1";

export const PropertyForm = ({ config, existing, onSaved, onCancel }) => {
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(existing ? {
    name: existing.name || "", property_type: existing.property_type || "hotel",
    city: existing.location?.city || "", state: existing.location?.state || "", country: existing.location?.country || "India",
    address: existing.location?.address || "", phone: existing.contact?.phone || "", whatsapp: existing.contact?.whatsapp || "",
    email: existing.contact?.email || "", website: existing.contact?.website || "", booking_url: existing.contact?.booking_url || "",
    description: existing.description || "", distinctive: existing.distinctive || "", ideal_guest: existing.ideal_guest || "",
    features: (existing.features || []).join(", "), photos: (existing.photos || []).join("\n"),
    nearest_airport: existing.location_intel?.nearest_airport || "", best_season: existing.location_intel?.best_season || "",
    rooms: existing.rooms?.length ? existing.rooms : [{ name: "", occupancy: 2, base_rate: "", amenities: "" }],
  } : {
    name: "", property_type: "hotel", city: "", state: "Kerala", country: "India", address: "",
    phone: "", whatsapp: "", email: "", website: "", booking_url: "",
    description: "", distinctive: "", ideal_guest: "", features: "", photos: "",
    nearest_airport: "", best_season: "",
    rooms: [{ name: "", occupancy: 2, base_rate: "", amenities: "" }],
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setRoom = (i, k) => (e) => {
    const rooms = [...f.rooms]; rooms[i] = { ...rooms[i], [k]: e.target.value }; setF({ ...f, rooms });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      name: f.name, property_type: f.property_type,
      location: { city: f.city, state: f.state, country: f.country, address: f.address },
      contact: { phone: f.phone, whatsapp: f.whatsapp, email: f.email, website: f.website, booking_url: f.booking_url },
      description: f.description, distinctive: f.distinctive, ideal_guest: f.ideal_guest,
      features: f.features.split(",").map((s) => s.trim()).filter(Boolean),
      photos: f.photos.split("\n").map((s) => s.trim()).filter(Boolean),
      location_intel: { nearest_airport: f.nearest_airport, best_season: f.best_season },
      rooms: f.rooms.filter((r) => r.name).map((r) => ({ ...r, occupancy: Number(r.occupancy) || 2, amenities: typeof r.amenities === "string" ? r.amenities.split(",").map((s) => s.trim()).filter(Boolean) : r.amenities })),
    };
    try {
      if (existing) await api.put(`/directstay/properties/${existing.id}`, payload);
      else await api.post("/directstay/properties", payload);
      toast.success(existing ? "Property updated" : "Property submitted for verification");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save property");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="property-form" className="border border-zinc-300 bg-white p-5 space-y-4">
      <div className="font-heading font-900 text-xl">{existing ? "Edit property" : "List your property"}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className={L}>Property name *</label><input data-testid="prop-name" required className={F} value={f.name} onChange={set("name")} /></div>
        <div><label className={L}>Type *</label>
          <select data-testid="prop-type" className={F} value={f.property_type} onChange={set("property_type")}>
            {(config?.property_types || []).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select></div>
        <div><label className={L}>City *</label><input data-testid="prop-city" required className={F} value={f.city} onChange={set("city")} /></div>
        <div><label className={L}>State *</label><input data-testid="prop-state" required className={F} value={f.state} onChange={set("state")} /></div>
        <div><label className={L}>Country</label><input className={F} value={f.country} onChange={set("country")} /></div>
        <div><label className={L}>Address</label><input className={F} value={f.address} onChange={set("address")} /></div>
        <div><label className={L}>Phone</label><input className={F} value={f.phone} onChange={set("phone")} /></div>
        <div><label className={L}>WhatsApp</label><input className={F} value={f.whatsapp} onChange={set("whatsapp")} /></div>
        <div><label className={L}>Email</label><input type="email" className={F} value={f.email} onChange={set("email")} /></div>
        <div><label className={L}>Website</label><input className={F} placeholder="https://" value={f.website} onChange={set("website")} /></div>
        <div><label className={L}>Direct booking URL</label><input className={F} placeholder="https://" value={f.booking_url} onChange={set("booking_url")} /></div>
        <div><label className={L}>Nearest airport</label><input className={F} placeholder="Kochi (COK) — 90 min" value={f.nearest_airport} onChange={set("nearest_airport")} /></div>
      </div>
      <div><label className={L}>Description</label><textarea rows={3} className={F} value={f.description} onChange={set("description")} placeholder="What travelers should know about your property" /></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className={L}>What makes it distinctive</label><input className={F} value={f.distinctive} onChange={set("distinctive")} /></div>
        <div><label className={L}>Ideal guest</label><input className={F} placeholder="Families, honeymooners…" value={f.ideal_guest} onChange={set("ideal_guest")} /></div>
      </div>
      <div><label className={L}>Features (comma separated)</label><input className={F} placeholder="Pool, Restaurant, Airport transfer, Ayurveda spa" value={f.features} onChange={set("features")} /></div>
      <div><label className={L}>Photo URLs (one per line)</label><textarea rows={2} className={F} value={f.photos} onChange={set("photos")} /></div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className={L}>Room types</label>
          <button type="button" data-testid="add-room-btn" onClick={() => setF({ ...f, rooms: [...f.rooms, { name: "", occupancy: 2, base_rate: "", amenities: "" }] })}
            className="text-xs font-600 text-emerald-700 hover:underline">+ Add room</button>
        </div>
        {f.rooms.map((r, i) => (
          <div key={i} className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
            <input className={F} placeholder="Room name" value={r.name} onChange={setRoom(i, "name")} />
            <input className={F} type="number" min="1" placeholder="Sleeps" value={r.occupancy} onChange={setRoom(i, "occupancy")} />
            <input className={F} placeholder="Base rate e.g. ₹4500" value={r.base_rate} onChange={setRoom(i, "base_rate")} />
            <input className={F} placeholder="Amenities (comma sep.)" value={Array.isArray(r.amenities) ? r.amenities.join(", ") : r.amenities} onChange={setRoom(i, "amenities")} />
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button data-testid="prop-submit" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white font-700 px-6 py-2.5 text-sm uppercase tracking-wide disabled:opacity-50">
          {busy ? "Saving…" : existing ? "Save changes" : "Submit for verification"}
        </button>
        {onCancel && <button type="button" onClick={onCancel} className="border border-zinc-300 px-6 py-2.5 text-sm font-600">Cancel</button>}
      </div>
      {!existing && <p className="text-xs text-zinc-500">Listings are reviewed by our team before publication. During the pilot, all plans are free.</p>}
    </form>
  );
};
